import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TableContainer,
  Paper,
  Table,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
  Tabs,
  Tab,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import PaginantionComponent from "../../../components/common/Pagination/PaginationComponent";
import SearchBar from "../../../components/common/SearchBar/SearchBar";
import { API_ENDPOINT } from "../../../services/api/resources";
import { nextPageClick, previouPageClick } from "../../../utils/pagination";

const PRICE_LIST_PAGE_SIZE = 10;

const formatSavedDate = (value) => {
  if (!value) return "-";
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return value;
  return parsedDate.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const normalizeSavedPriceCatalogRecord = (record) => {
  const savedRecord = record?.saved_record || record || {};
  const catalog = record?.catalog || {};
  const filterDocument = record?.filter_document || {};
  const detection = record?.detection || {};
  const extraction = record?.extraction || {};
  const results = record?.results || {};
  const matchedItems = Array.isArray(results?.matched_items)
    ? results.matched_items
    : Array.isArray(savedRecord?.matched_items)
      ? savedRecord.matched_items
      : [];
  const filterResults = Array.isArray(results?.filter_results)
    ? results.filter_results
    : Array.isArray(savedRecord?.filter_results)
      ? savedRecord.filter_results
      : [];
  const priceCatalogFileName =
    savedRecord?.price_catalog_file_name ||
    catalog?.file_name ||
    savedRecord?.catalog_file ||
    "-";
  const filterDocumentFileName =
    savedRecord?.filter_document_file_name ||
    filterDocument?.file_name ||
    savedRecord?.filter_file ||
    "-";

  return {
    ...savedRecord,
    token: savedRecord?.token || savedRecord?.id || priceCatalogFileName,
    document_number: priceCatalogFileName,
    price_catalog_file_name: priceCatalogFileName,
    filter_document_file_name: filterDocumentFileName,
    catalog_source_type:
      savedRecord?.catalog_source_type || catalog?.source_type || "-",
    document_mode:
      savedRecord?.document_mode || detection?.document_mode || "-",
    filter_set_count:
      savedRecord?.filter_set_count ?? detection?.filter_set_count ?? "-",
    matched_count:
      results?.total_filter_matches ??
      savedRecord?.total_filter_matches ??
      savedRecord?.matched_count ??
      matchedItems.length,
    unique_matched_count:
      results?.unique_matched_count ?? savedRecord?.unique_matched_count ?? "-",
    created_date: formatSavedDate(savedRecord?.created_at || savedRecord?.created_date),
    buyer_name: savedRecord?.buyer_name || "-",
    vendor_name: savedRecord?.vendor_name || "-",
    line_items: matchedItems,
    filter_results: filterResults,
    raw_filter_response: {
      ...savedRecord,
      ...detection,
      ...extraction,
      matched_items: matchedItems,
      filter_results: filterResults,
    },
  };
};

const recordMatchesSearch = (record, search) => {
  if (!search) return true;
  const normalizedSearch = search.toLowerCase();
  return [
    record?.document_number,
    record?.price_catalog_file_name,
    record?.filter_document_file_name,
    record?.catalog_source_type,
    record?.document_mode,
  ].some((value) => String(value || "").toLowerCase().includes(normalizedSearch));
};

const PriceListHome = ({
  onUploadPriceCatalog,
  onUploadPriceList,
  catalogRecords,
  priceListRecords,
}) => {
  const loginData = useSelector((state) => state.loginData.value);
  const [activeTab, setActiveTab] = useState(0);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [priceListSearch, setPriceListSearch] = useState("");
  const [debouncedPriceListSearch, setDebouncedPriceListSearch] = useState("");
  const [priceListPageNumber, setPriceListPageNumber] = useState(1);
  const [savedPriceListRecords, setSavedPriceListRecords] = useState([]);
  const [priceListPagination, setPriceListPagination] = useState({
    page: 1,
    page_size: PRICE_LIST_PAGE_SIZE,
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_previous: false,
    count: 0,
  });
  const [priceListLoading, setPriceListLoading] = useState(false);
  const [priceListError, setPriceListError] = useState("");
  const navigate = useNavigate();
  const organizationToken =
    loginData?.organization_token ||
    loginData?.org_token ||
    loginData?.user?.organization_token ||
    loginData?.user?.org_token ||
    "";
  const authToken =
    loginData?.auth_token ||
    loginData?.access_token ||
    loginData?.token ||
    loginData?.user?.auth_token ||
    "";

  const filteredCatalogRecords = catalogRecords.filter(
    (item) =>
      item.document_number?.toLowerCase().includes(catalogSearch.toLowerCase()) ||
      item.buyer_name?.toLowerCase().includes(catalogSearch.toLowerCase()) ||
      item.vendor_name?.toLowerCase().includes(catalogSearch.toLowerCase()),
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedPriceListSearch(priceListSearch.trim());
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [priceListSearch]);

  useEffect(() => {
    if (activeTab !== 1) return;

    const fetchSavedPriceListRecords = async () => {
      if (!organizationToken) {
        setPriceListError("Organization token not found. Please login again.");
        setSavedPriceListRecords([]);
        setPriceListPagination((prev) => ({ ...prev, total_count: 0, total_pages: 0 }));
        return;
      }

      try {
        setPriceListLoading(true);
        setPriceListError("");

        const headers = {
          "Content-Type": "application/json",
        };
        if (authToken) {
          headers.Authorization = `Token ${authToken}`;
        }

        const response = await axios.post(
          API_ENDPOINT.get_price_catalog_filter,
          {
            organization_token: organizationToken,
            page: Number(priceListPageNumber) || 1,
            page_size: PRICE_LIST_PAGE_SIZE,
            search: debouncedPriceListSearch,
            filters: {
              document_mode: "multiple_independent_filters",
              catalog_source_type: "spreadsheet",
              extraction_complete: true,
            },
          },
          { headers },
        );

        const payload = response?.data || {};
        if (payload?.status === false || payload?.success === false) {
          setPriceListError(payload?.message || "Unable to fetch saved price lists.");
          setSavedPriceListRecords([]);
          return;
        }

        const responseData = payload?.data || {};
        setSavedPriceListRecords(
          (Array.isArray(responseData?.records) ? responseData.records : []).map(
            normalizeSavedPriceCatalogRecord,
          ),
        );
        setPriceListPagination({
          page: responseData?.pagination?.page || Number(priceListPageNumber) || 1,
          page_size: responseData?.pagination?.page_size || PRICE_LIST_PAGE_SIZE,
          total_count: responseData?.pagination?.total_count || 0,
          total_pages: responseData?.pagination?.total_pages || 0,
          has_next: Boolean(responseData?.pagination?.has_next),
          has_previous: Boolean(responseData?.pagination?.has_previous),
          count: responseData?.pagination?.count || 0,
        });
      } catch (error) {
        console.error("Error fetching saved price catalog filters:", error);
        setPriceListError(
          error?.response?.data?.message ||
            error?.response?.data?.response ||
            "Unable to fetch saved price lists. Please try again.",
        );
        setSavedPriceListRecords([]);
      } finally {
        setPriceListLoading(false);
      }
    };

    fetchSavedPriceListRecords();
  }, [
    activeTab,
    authToken,
    debouncedPriceListSearch,
    organizationToken,
    priceListPageNumber,
  ]);

  const filteredLocalPriceListRecords = useMemo(
    () =>
      priceListRecords
        .map((record) => normalizeSavedPriceCatalogRecord(record))
        .filter((record) => recordMatchesSearch(record, debouncedPriceListSearch)),
    [debouncedPriceListSearch, priceListRecords],
  );

  const displayedPriceListRecords = useMemo(
    () => {
      const savedRecordKeys = new Set(
        savedPriceListRecords.map((record) => String(record?.token || record?.id || "")),
      );
      const localOnlyRecords = filteredLocalPriceListRecords.filter(
        (record) => !savedRecordKeys.has(String(record?.token || record?.id || "")),
      );
      return [...localOnlyRecords, ...savedPriceListRecords];
    },
    [filteredLocalPriceListRecords, savedPriceListRecords],
  );
  const priceListTotalCount = Math.max(
    priceListPagination.total_count,
    displayedPriceListRecords.length,
  );
  const priceListTotalPages =
    priceListPagination.total_pages || (displayedPriceListRecords.length > 0 ? 1 : 0);

  const handleAction = (record) => {
    navigate("/Buyer_dashboard/price_list/create_quotation", {
      state: { selectedPriceList: record },
    });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          mt: 1,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <HeaderTitle titleText="Price List" />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            sx={{
              fontSize: 15,
              fontFamily:"gilroyBold",
              textTransform: "none",
              px: 1,
              py: 1,
              borderRadius: 1.5,
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            }}
            onClick={onUploadPriceCatalog}
          >
            Price Catalog
          </Button>
          <Button
            variant="contained"
            size="small"
            sx={{
              fontSize: 15,
              textTransform: "none",
              fontFamily:"gilroyBold",
              px: 1,
              py: 1,
              borderRadius: 1.5,
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            }}
            onClick={onUploadPriceList}
          >
            Generate Quotation
          </Button>
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 1 }}>
        <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)}>
          <Tab
            label="Price Catalog"
            sx={{ textTransform: "none", fontFamily:"gilroyBold",fontSize: "16px" }}
          />
          <Tab
            label="Quotation"
            sx={{ textTransform: "none",fontFamily:"gilroyBold", fontSize: "16px"}}
          />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <Box sx={{ mt: "20px", width: "100%", overflowX: "auto" }}>
          <Box sx={{ flex: 1, mb: 2 }}>
            <SearchBar
              value={catalogSearch}
              onChange={(value) => {
                setCatalogSearch(value);
              }}
            />
          </Box>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 2,
              boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    "Price Catalog Number",
                    "Buyer Name",
                    "Supplier Name",
                    "created_date",
                    "Action",
                  ].map((header, index) => (
                    <TableCell
                      key={index}
                      sx={{
                        fontWeight: 600,
                        fontFamily: "GilroySemiBold",
                        backgroundColor: "#e0e9ee",
                        fontSize: "14px",
                        borderBottom: "1px solid #e2e8f0",
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredCatalogRecords.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      backgroundColor: "white",
                      "&:hover": { backgroundColor: "#f5f5f5" },
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    <TableCell sx={{ fontFamily: "GilroyMedium", fontSize: "14px" }}>
                      {item.document_number}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "GilroyMedium", fontSize: "14px" }}>
                      {item.buyer_name}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "GilroyMedium", fontSize: "14px" }}>
                      {item.vendor_name}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "GilroyMedium", fontSize: "14px" }}>
                      {item.created_date}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="medium"
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          borderRadius: 1.5,
                          fontSize: "12px",
                          whiteSpace: "nowrap",
                        }}
                        onClick={() => handleAction(item)}
                      >
                        Create Quotation
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}

                {filteredCatalogRecords.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      sx={{
                        fontFamily: "GilroyMedium",
                        fontSize: "14px",
                        textAlign: "center",
                      }}
                    >
                      No price catalog records found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {activeTab === 1 && (
        <Box sx={{ mt: "20px", width: "100%", overflowX: "auto" }}>
          <Box sx={{ flex: 1, mb: 2 }}>
            <SearchBar
              value={priceListSearch}
              onChange={(value) => {
                setPriceListSearch(value);
                setPriceListPageNumber(1);
              }}
            />
          </Box>
          {priceListError ? (
            <Typography sx={{ color: "#b91c1c", fontSize: 14, mb: 1 }}>
              {priceListError}
            </Typography>
          ) : null}
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 2,
              boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    "Price Catalog",
                    "Filter Document",
                    "Source Type",
                    "Filter Sets",
                    "Matched Items",
                    "Unique Items",
                    "Created Date",
                    "Action",
                  ].map((header, index) => (
                      <TableCell
                        key={index}
                        sx={{
                          fontWeight: 600,
                          fontFamily: "GilroySemiBold",
                          backgroundColor: "#e0e9ee",
                          fontSize: "14px",
                          borderBottom: "1px solid #e2e8f0",
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {priceListLoading && (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      sx={{
                        fontFamily: "GilroyMedium",
                        fontSize: "14px",
                        textAlign: "center",
                        py: 4,
                      }}
                    >
                      <CircularProgress size={22} />
                    </TableCell>
                  </TableRow>
                )}

                {!priceListLoading && displayedPriceListRecords.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      backgroundColor: "white",
                      "&:hover": { backgroundColor: "#f5f5f5" },
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    <TableCell sx={{ fontFamily: "GilroyMedium", fontSize: "14px" }}>
                      {item.price_catalog_file_name}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "GilroyMedium", fontSize: "14px" }}>
                      {item.filter_document_file_name}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "GilroyMedium", fontSize: "14px" }}>
                      {item.catalog_source_type}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "GilroyMedium", fontSize: "14px" }}>
                      {item.filter_set_count}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "GilroyMedium", fontSize: "14px" }}>
                      {item.matched_count}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "GilroyMedium", fontSize: "14px" }}>
                      {item.unique_matched_count}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "GilroyMedium", fontSize: "14px" }}>
                      {item.created_date}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="medium"
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          borderRadius: 1.5,
                          fontSize: "12px",
                          whiteSpace: "nowrap",
                        }}
                        onClick={() => handleAction(item)}
                      >
                        Create Quotation
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}

                {!priceListLoading && displayedPriceListRecords.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      sx={{
                        fontFamily: "GilroyMedium",
                        fontSize: "14px",
                        textAlign: "center",
                      }}
                    >
                      No price list records found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <PaginantionComponent
            currentPage={priceListPagination.page}
            itemsPerPage={priceListTotalCount}
            totalItems={priceListTotalPages}
            disablePrevious={!priceListPagination.has_previous}
            disableNext={!priceListPagination.has_next}
            previouPageClick={() =>
              previouPageClick(priceListPageNumber, setPriceListPageNumber)
            }
            nextPageClick={() =>
              nextPageClick(priceListPageNumber, setPriceListPageNumber, {
                page_count: priceListTotalPages,
              })
            }
            pageNumber={priceListPageNumber}
            setPageNumber={setPriceListPageNumber}
          />
        </Box>
      )}
    </Box>
  );
};

export default PriceListHome;
